import React, { useCallback, useEffect, useRef, useState } from 'react'
import NetworkView, { NetworkView3Ref } from '../networkView/NetworkView'
import { CandidateLiteDto } from '../../api/role/models/CandidateDto'
import { MapExtra } from '../../utils/MapExtra'
import { PersonDto } from '../../api/person/models/PersonDto'
import { CompanyDto } from '../../api/company/models/CompanyDto'
import { ResolvedPersonLiteDto } from '../../api/network/models/getNetworkScopedDtos'
import { PersonType, SDManager } from '../networkView/SDManager'
import companyApi from '../../api/company/CompanyApi'

type Props = {
    resolvedPersons: ResolvedPersonLiteDto[]
    networkLinksPersonCache: MapExtra<string, PersonDto>
    candidatesPersonCache: MapExtra<string, PersonDto>
    companyCache: MapExtra<string, CompanyDto>
    hasRole?: boolean
    onCandidateSelection?: (candidateId: string) => void
}

function HelixSearchNetworkView({
    resolvedPersons,
    networkLinksPersonCache,
    candidatesPersonCache,
    companyCache,
    hasRole = false,
    onCandidateSelection,
}: Props): React.ReactNode {
    const networkViewRef = useRef<NetworkView3Ref | null>(null)
    const [sdManager, setSdManager] = useState<SDManager | null>(null)
    const prevResolvedPersonIdsRef = useRef<string[]>([])

    const handleNetworkViewRef = useCallback(
        (node: NetworkView3Ref | null) => {
            networkViewRef.current = node
            if (node?.sdManager && !sdManager) {
                setSdManager(node.sdManager)
            }
        },
        [sdManager],
    )

    useEffect(() => {
        if (!sdManager || !onCandidateSelection) return

        sdManager.registerOnPersonSelected((person, personType) => {
            if (personType === PersonType.CANDIDATE && person?.id) {
                onCandidateSelection(person.id)
            }
        })
        sdManager.registerOnPersonHovered((person, personType) => {
            if (personType === PersonType.CANDIDATE && person?.id) {
                onCandidateSelection(person.id)
            }
        })
    }, [sdManager, onCandidateSelection])

    useEffect(() => {
        if (!sdManager || !networkViewRef.current) return

        const currentIds = resolvedPersons.map((p) => p.personId).sort()
        const previousIds = prevResolvedPersonIdsRef.current

        const isSame =
            currentIds.length === previousIds.length &&
            currentIds.every((id, idx) => id === previousIds[idx])

        if (isSame) return
        prevResolvedPersonIdsRef.current = currentIds

        // this is a stick plaster fix. For some reason the companyCache is sometimes
        //  missing companies. I've allowed the SDManager to call into the API via this callback
        //  if a company is missing.
        sdManager.setCaches(
            candidatesPersonCache,
            companyCache,
            async (companyId: string) => {
                // the company is not in the cache, go and get it from the api
                const result = await companyApi.getCompanies({
                    companyIds: [companyId],
                })
                if (
                    !result ||
                    !result.companies ||
                    result.companies.length === 0
                ) {
                    throw new Error(
                        `Company with ID ${companyId} not found in the API response. HelixSearchNetworkView sdManager company getter.`,
                    )
                }
                return result.companies[0]
            },
        )
        sdManager.setShowStatusInfo(hasRole)
        sdManager.reset()

        const connectedNetworkLinksIds = new Set<string>()
        resolvedPersons.forEach((person) => {
            person.connectionDetails.forEach((connection) => {
                connectedNetworkLinksIds.add(connection.seedPersonId)
            })
        })

        const connectedNetworkLinksPersons = Array.from(
            networkLinksPersonCache.values(),
        ).filter((person) => connectedNetworkLinksIds.has(person.id))

        sdManager.initialiseSeedPersons(connectedNetworkLinksPersons, [])

        const candidates: CandidateLiteDto[] = resolvedPersons.map(
            (person) => ({
                candidatePersonId: person.personId,
                contacted: false,
                connections: person.connectionDetails.map((connection) => ({
                    connectedPersonId: connection.seedPersonId,
                    sharedWorkHistory: connection.sharedWorkHistory,
                })),
                companyConnections: person.companyConnectionDetails.map(
                    (company) => ({
                        connectedCompanyId: company.companyId,
                    }),
                ),
                rankingScore: 0,
                closenessScore: 0,
                jobRelevanceScore: 0,
                jobRelevanceRawScore: 0,
                jobRelevanceJobTitle: '',
            }),
        )

        sdManager.addCandidates(candidates)
    }, [
        resolvedPersons,
        sdManager,
        networkLinksPersonCache,
        candidatesPersonCache,
        companyCache,
        hasRole,
    ])

    return <NetworkView ref={handleNetworkViewRef} />
}

export default HelixSearchNetworkView
